import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { TokenService } from './service/token.service';
import {  Observable } from 'rxjs';
import { HttpErrorsService } from '../core/services/http-errors.service';
import { Config } from 'ng-otp-input/lib/models/config';
import { orgConfig } from 'src/app/dataModel/config.model';

export let APP_CONFIG = new InjectionToken<Config>('APP_CONFIG');

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  private loginHeaders = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept-Language': 'de' });
  liveBaseUrl = '';
  imgReviewUrl = '';
  api: string = "api";
  cdnPreviewPath = "cdn/preview"
  weSocketPath = "ws"

  constructor(
    public tokenService: TokenService,
    private http: HttpClient,
    public errorService: HttpErrorsService,
    @Inject(APP_CONFIG) private environment: orgConfig
  ) {
    this.liveBaseUrl = this.apiUrl;
    this.imgReviewUrl = this.cloudlStorageUrl;
  }

  get config(): orgConfig {
    return this.environment;
  }

  get cloudlStorageUrl():string {
    return `${this.environment.filePreviewUrl}`;
  }

  get websocketUrl():string {
    return `${this.environment.apiUrl}/${this.weSocketPath}`;
  }

  get apiUrl(): string {
    return `${this.environment.apiUrl}`;
  }
  
  getCommonHeaders(): HttpHeaders {
    let commonHeaders = new HttpHeaders();
    const authToken = this.tokenService.getToken();
    if (authToken) {
      commonHeaders = commonHeaders.set('Authorization', `Bearer ${authToken}`);
    }
    // Set Content-Type to application/json
    commonHeaders = commonHeaders.set('Content-Type', 'application/json');
    return commonHeaders;
  }

  getFormDataHeader(): HttpHeaders {
    let formDataHeader = new HttpHeaders();
    const authToken = this.tokenService.getToken();
    if (authToken) {
      formDataHeader = formDataHeader.set(
        'Authorization',
        `Bearer ${authToken}`
      );
    }
    // Set Content-Type to multipart/form-data
    formDataHeader = formDataHeader.set('Content-Type', 'multipart/form-data');
    return formDataHeader;
  }
  getLoginHeaders(): HttpHeaders {
    return this.loginHeaders;
  }

  headerWithToken() {
    let Authorization: string = '';
    const token = this.tokenService.getToken();
    if (token) {
      Authorization = 'Bearer ' + token;
    } else {
      this.errorService.handleErrorAction('Unauthorized', 401);
    }
    const header: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization,
      }),
      observe: 'response',
    };
    return header;
  }

  headerWithOutToken() {
    const header: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      observe: 'response',
    };
    return header;
  }

  headerWithTokenFormData() {
    let Authorization: string = '';
    const token = this.tokenService.getToken();
    if (token) {
      Authorization = 'Bearer ' + token;
    } else {
      this.errorService.handleErrorAction('Unauthorized', 401);
    }
    const header: any = {
      headers: new HttpHeaders({ Authorization }),
      observe: 'response',
    };
    return header;
  }

  headerWithTokenPatchData() {
    let Authorization: string = '';
    const token = this.tokenService.getToken();
    if (token) {
      Authorization = 'Bearer ' + token;
    } else {
      this.errorService.handleErrorAction('Unauthorized', 401);
    }
    const header: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': 'true',
        Authorization,
      }),
      observe: 'response',
    };
    return header;
  }

  get(path: string, _params: HttpParams = new HttpParams()): Observable<any> {
    return this.http.get(`${this.liveBaseUrl}/${this.api}/${path}`, this.headerWithToken());
  }

  post(path: string, body: Object = {}): Observable<any> {
    return this.http.post(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      JSON.stringify(body),
      this.headerWithToken()
    );
  }

  postWithoutToken(path: string, body: Object = {}): Observable<any> {
    return this.http.post(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      JSON.stringify(body),
      this.headerWithOutToken()
    );
  }

  put(path: string, body: Object = {}): Observable<any> {
    return this.http.put(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      JSON.stringify(body),
      this.headerWithToken()
    );
  }

  putFormData(path: string, formdata: FormData) {
    return this.http.put(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      formdata,
      this.headerWithTokenFormData()
    );
  }

  postFormData(path: string, formdata: FormData) {
    return this.http.post(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      formdata,
      this.headerWithTokenFormData()
    );
  }
  patchFormData(path: string, formdata: FormData) {
    return this.http.patch(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      formdata,
      this.headerWithTokenFormData()
    );
  }
  patch(path: string, body: Object = {}): Observable<any> {
    return this.http.patch(
      `${this.liveBaseUrl}/${this.api}/${path}`,
      JSON.stringify(body),
      this.headerWithToken()
    );
  }

  patchWithoutBody(path: string): Observable<any> {
    const token = this.tokenService.getToken();
    let headers = new HttpHeaders({ Accept: 'application/json' });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      this.errorService.handleErrorAction('Unauthorized', 401);
    }

    return this.http.patch(`${this.liveBaseUrl}/${this.api}/${path}`, null, {
      headers,
      observe: 'response',
    });
  }

  postForFile(
    path: string,
    body: Object = {}
  ): Observable<HttpResponse<Blob>> {
    let Authorization = '';
    const token = this.tokenService.getToken();

    if (token) {
      Authorization = `Bearer ${token}`;
    } else {
      this.errorService.handleErrorAction('Unauthorized', 401);
    }

    return this.http.post(`${this.liveBaseUrl}/${this.api}/${path}`, body, {
      headers: new HttpHeaders({
        Authorization,
        Accept: 'text/plain'
      }),
      observe: 'response',
      responseType: 'blob'
    });
  }

  delete(path: string, _params: HttpParams = new HttpParams()): Observable<any> {
    return this.http.delete(`${this.liveBaseUrl}/${this.api}/${path}`, this.headerWithToken());
  }

  deleteWithPayload(path: string, params: any = {}): Observable<any> {
    return this.http.request('DELETE', `${this.liveBaseUrl}/${this.api}/${path}`, {
        body: params, // Sending params as body
        headers: this.headerWithToken().headers, // Attach the headers with token
        observe: 'response'
    });
  }
}
